// Team presence panel on the Overview (#812).
//
// The panel says one thing about each colleague — they have a live Cockpit session, or they do
// not, or we could not tell. It never says more, because the product does not know more: there is
// no last-seen time to show and no history to open. When the service cannot be reached everyone
// becomes "unknown" rather than keeping a state that would go on looking current while ageing.
import React from "react";
import { cockpit } from "@/api";
import { cachedRead } from "@/api/read-cache";
import type { PresenceView } from "@/api";
import { Icon } from "@/lib";
import { useT } from "../i18n";
import {
  heartbeatIntervalMs,
  presenceModel,
  type DisplayPresenceState,
  type PresencePanelState,
} from "./presence.logic";

function stateLabelKey(state: DisplayPresenceState): string {
  return `overview.presence.state.${state}`;
}

export interface OverviewPresenceSummary {
  state: PresencePanelState;
  onlineCount: number | null;
  total: number;
}

export interface PresencePanelProps {
  onSummaryChange?: (summary: OverviewPresenceSummary) => void;
  onOpenStaff?: () => void;
}

export function PresencePanel({ onSummaryChange, onOpenStaff }: PresencePanelProps = {}) {
  const t = useT();
  // #1536: the roster from the last beat stays on screen until this visit's first beat answers.
  const [view, setView] = React.useState<PresenceView | null>(() => cachedRead("presenceHeartbeat") ?? null);
  const [failed, setFailed] = React.useState(false);

  /**
   * One beat, resolving to the answer it got — or null when it could not get one.
   *
   * The answer is returned rather than only stored because the caller needs it to decide when to
   * beat again, and a state variable is not readable that soon: `setView` has not been committed
   * by the time this promise settles, so a timer reading it back would always see the previous
   * render's value and re-arm on the wrong interval.
   */
  const beat = React.useCallback((): Promise<PresenceView | null> => {
    // The heartbeat is the read: a live Cockpit has to refresh its own presence anyway, so making
    // that the same request keeps the panel current without a second poll that could drift.
    return cockpit
      .presenceHeartbeat()
      .then((next) => {
        setView(next);
        setFailed(false);
        return next;
      })
      .catch(() => {
        setFailed(true);
        return null;
      });
  }, []);

  // The cadence last agreed with the server, so a failed beat keeps asking at the same rate
  // instead of dropping to the fallback and taking longer to notice the service came back.
  const cadence = React.useRef<PresenceView | null>(null);

  React.useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = () => {
      void beat().then((answer) => {
        if (!live) return;
        if (answer) cadence.current = answer;
        // Re-armed after each answer rather than on a fixed interval, so a slow or failing server
        // cannot stack requests on top of each other.
        timer = setTimeout(tick, heartbeatIntervalMs(cadence.current));
      });
    };
    tick();
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
    };
  }, [beat]);

  const model = presenceModel({ view, failed });

  React.useEffect(() => {
    onSummaryChange?.({
      state: model.state,
      onlineCount: model.onlineCount,
      total: model.total,
    });
  }, [model.onlineCount, model.state, model.total, onSummaryChange]);

  return (
    <section
      className="ov-card ov-presence"
      aria-label={t("overview.presence.title")}
      data-testid="presence-panel"
      data-state={model.state}
      aria-busy={model.state === "loading" ? true : undefined}
    >
      <div className="ov-card__head">
        <h2>{t("overview.presence.title")}</h2>
        {model.onlineCount !== null && (
          <span className="ov-presence__count" data-testid="presence-count">
            {t("overview.presence.onlineOf", { n: model.onlineCount, total: model.total })}
          </span>
        )}
      </div>

      {model.state === "unavailable" && (
        <p className="ov-presence__notice" role="status" data-testid="presence-unavailable">
          {t("overview.presence.unavailable")}
        </p>
      )}

      {model.state === "loading" && (
        <p className="ov-presence__notice">{t("overview.presence.loading")}</p>
      )}

      {model.state === "empty" && (
        <p className="ov-presence__notice" data-testid="presence-empty">
          {t("overview.presence.empty")}
        </p>
      )}

      <ul className="ov-presence__list">
        {model.rows.map((row) => (
          <li
            className="ov-presence__row"
            key={row.userId}
            data-testid={`presence-row-${row.userId}`}
            data-presence={row.state}
          >
            {/* The dot is decoration; the state is written out, so it does not depend on colour
                or on a tooltip nobody on a phone can reach. */}
            <span className={`ov-presence__dot ov-presence__dot--${row.state}`} aria-hidden="true" />
            <span className="ov-presence__who">
              <span className="ov-presence__name">{row.label}</span>
              <span className="ov-presence__role">{t(`role.${row.role}`, undefined, row.role)}</span>
            </span>
            <span className="ov-presence__state">{t(stateLabelKey(row.state))}</span>
          </li>
        ))}
      </ul>

      {onOpenStaff && (
        <button type="button" className="ov-presence__action" onClick={onOpenStaff}>
          {t("overview.presence.openStaffArea")}
          <Icon name="chevronRight" size={14} />
        </button>
      )}
    </section>
  );
}
